import React, { useState, useEffect, useCallback } from "react";
import { Calendar as CalendarIcon, Clock, CheckCircle, Truck, User, Phone, MapPin, Sparkles, Send, Loader2, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";

const BOOKING_API_URL = "https://backend.leadconnectorhq.com";
const VIBE_API_URL = "https://backend.leadconnectorhq.com/vibe-ai";
const CALENDAR_ID = "LqIj47SOYjczvoClislH";
const LOCATION_ID = "xfaKqeYCXXqhpM9mQvnB";
const SERVICE_TYPE_FIELD_ID = "wkUEM1hghWoHvXSXvuGI";

const getBrowserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const clampAvailabilityRange = (startMs: number, endMs: number) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = Math.max(startMs, today.getTime());
  const maxEndDate = startDate + 31 * 24 * 60 * 60 * 1000;
  const endDate = Math.min(Math.max(endMs, startDate), maxEndDate);
  return { startDate, endDate };
};

const fetchCalendarFreeSlots = async (calendarId: string, startMs: number, endMs: number) => {
  const { startDate, endDate } = clampAvailabilityRange(startMs, endMs);
  const params = new URLSearchParams({
    startDate: String(startDate),
    endDate: String(endDate),
    timezone: getBrowserTimezone(),
  });
  const response = await fetch(`${BOOKING_API_URL}/calendars/${calendarId}/free-slots?${params}`);
  if (!response.ok) throw new Error("Failed to fetch calendar availability");
  return response.json();
};

const submitCalendarBooking = async (payload: Record<string, unknown>) => {
  const response = await fetch(`${VIBE_API_URL}/booking/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...payload,
      selectedTimezone: (payload.selectedTimezone as string) ?? getBrowserTimezone(),
      sessionId: (payload.sessionId as string) ?? crypto.randomUUID(),
      customFields: payload.customFields ?? [],
    }),
  });
  if (!response.ok) throw new Error("Booking submission failed");
  return response.json();
};

type SlotsMap = Record<string, { slots: string[] }>;

export const BookingCalendarSection: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");

  // Form State
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [notes, setNotes] = useState("");

  // Availability state
  const [slotsMap, setSlotsMap] = useState<SlotsMap>({});
  const [slotsLoading, setSlotsLoading] = useState<boolean>(false);
  const [slotsError, setSlotsError] = useState<string>("");

  // Fetch slots for the visible month range
  const loadSlots = useCallback(async (baseDate: Date) => {
    setSlotsLoading(true);
    setSlotsError("");
    try {
      const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
      const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59);
      const data = await fetchCalendarFreeSlots(CALENDAR_ID, start.getTime(), end.getTime());
      setSlotsMap(data as SlotsMap);
    } catch {
      setSlotsError("Could not load availability. Please call us at (954) 280-8090.");
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  // Defer initial slot load until section is near viewport (reduces 828ms critical path block)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadSlots(new Date());
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    
    const el = document.getElementById("booking");
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [loadSlots]);

  // When month changes via calendar navigation, reload
  const handleMonthChange = (date: Date) => {
    loadSlots(date);
  };

  // Get slots for the selected date
  const getSlotsForDate = (date: Date): string[] => {
    const key = date.toISOString().slice(0, 10);
    const entry = slotsMap[key];
    return entry?.slots ?? [];
  };

  // Format slot for display
  const formatSlotDisplay = (isoSlot: string): string => {
    try {
      const d = new Date(isoSlot);
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    } catch {
      return isoSlot;
    }
  };

  // Days that have available slots (for calendar highlighting)
  const availableDateKeys = Object.keys(slotsMap).filter((k) => slotsMap[k]?.slots?.length > 0);

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) {
      setSubmitError("Please select an available time slot.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      let bookingCreated = false;

      // METHOD 1: Submit booking via CRM booking API (creates the actual appointment)
      try {
        await submitCalendarBooking({
          locationId: LOCATION_ID,
          calendarId: CALENDAR_ID,
          firstName,
          lastName,
          email,
          phone,
          selectedSlot,
          notes: notes || undefined,
          address1: address || undefined,
          customFields: serviceType
            ? [{ id: SERVICE_TYPE_FIELD_ID, field_value: serviceType }]
            : [],
        });
        bookingCreated = true;
        console.log("[Booking] Appointment created successfully");
      } catch (bookingErr) {
        console.error("[Booking] Booking API error:", bookingErr);
      }

      // METHOD 2: Fire CRM tracking event (analytics + secondary contact capture)
      let trackingSuccess = false;
      try {
        const tracking = buildTrackingPayload("booking-calendar", "Junk Pickup Booking");
        tracking.formData = {
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          address1: address,
        };
        tracking.formLabels = {
          first_name: "First Name",
          last_name: "Last Name",
          email: "Email",
          phone: "Phone Number",
          address1: "Service Address",
        };
        trackingSuccess = await postTrackingEvent(tracking, {
          customFields: {
            [SERVICE_TYPE_FIELD_ID]: { value: serviceType, label: "Type of Junk / Service Needed" },
          },
        });
        console.log("[Booking] Tracking event result:", trackingSuccess);
      } catch (trackingErr) {
        console.warn("[Booking] Tracking event error:", trackingErr);
      }

      // Success if either method worked
      if (!bookingCreated && !trackingSuccess) {
        throw new Error("Both submission methods failed");
      }

      setSubmitted(true);
    } catch {
      setSubmitError("Booking failed. Please try again or call (954) 280-8090.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-12 sm:py-20 bg-slate-900 text-white relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-400 text-slate-950 font-black px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4">
            <CalendarIcon className="w-4 h-4" /> Pick Your Preferred Pickup Window
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading uppercase tracking-tight mb-4">
            Schedule Your <span className="text-amber-400">Davie Pickup</span> Online
          </h2>
          <p className="text-slate-300 text-base sm:text-lg">
            Choose your date and time slot. Same-day & next-day appointments guaranteed in Davie (33314, 33324, 33328, 33330, 33331) and Broward County!
          </p>
        </div>

        {submitted ? (
          <div className="max-w-2xl mx-auto bg-slate-800 border-2 border-emerald-500 rounded-3xl p-10 text-center shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="w-20 h-20 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-emerald-500/40">
              <CheckCircle className="w-10 h-10" />
            </div>
            <h3 className="text-3xl font-black font-heading text-white mb-2">
              Pickup Booking Confirmed!
            </h3>
            <p className="text-slate-300 text-lg mb-6">
              Thank you, <span className="text-amber-400 font-bold">{firstName || "Valued Customer"}</span>! Your appointment has been booked for{" "}
              <span className="text-amber-400 font-bold">
                {selectedDate?.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              </span>{" "}
              at <span className="text-amber-400 font-bold">{selectedSlot ? formatSlotDisplay(selectedSlot) : ""}</span>.
            </p>
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 text-sm text-slate-300 mb-8 max-w-md mx-auto text-left space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <span><strong className="text-white">Service Address:</strong> {address || "Davie FL Area"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-amber-400 shrink-0" />
                <span><strong className="text-white">Job Type:</strong> {serviceType || "Junk Removal Pickup"}</span>
              </div>
            </div>
            <p className="text-slate-400 text-xs mb-6">
              A confirmation has been sent to {email || "your email"}. Our team will call ({phone || "(954) 280-8090"}) to confirm on-site estimate details.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setSelectedSlot("");
                setSelectedDate(undefined);
              }}
              className="bg-primary hover:bg-rose-700 text-white font-extrabold uppercase px-8"
            >
              Book Another Pickup
            </Button>
          </div>
        ) : (
          <div className="bg-slate-800 border-2 border-slate-700 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-10 shadow-2xl">
            <form onSubmit={handleBookingSubmit} className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-8">
              
              {/* Calendar Column - Full width on mobile, left column on desktop */}
              <div className="lg:col-span-5 flex flex-col items-center bg-slate-900 border border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 overflow-x-auto">
                <div className="flex items-center gap-2 text-amber-400 font-black text-sm uppercase tracking-wider mb-4 w-full text-center justify-center shrink-0">
                  <CalendarIcon className="w-4 h-4 shrink-0" /> 1. Select Pickup Date
                </div>
                
                <div className="w-full flex justify-center shrink-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => {
                      setSelectedDate(d);
                      setSelectedSlot("");
                    }}
                    onMonthChange={handleMonthChange}
                    disabled={(date) => {
                      const key = date.toISOString().slice(0, 10);
                      return !availableDateKeys.includes(key);
                    }}
                    className="bg-slate-950 rounded-lg sm:rounded-xl border border-slate-800 p-2 sm:p-3 text-white pointer-events-auto scale-[0.85] sm:scale-100 origin-top"
                    classNames={{
                      caption: "text-sm sm:text-base",
                      head_cell: "text-[10px] sm:text-xs w-8 sm:w-9",
                      cell: "p-0 sm:p-1 text-xs sm:text-sm",
                      button: "h-8 w-8 sm:h-9 sm:w-9 text-xs sm:text-sm",
                      nav_button: "h-7 w-7 sm:h-9 sm:w-9",
                    }}
                  />
                </div>
                {slotsLoading && (
                  <div className="flex items-center gap-2 text-slate-400 text-xs mt-3 shrink-0">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading availability...
                  </div>
                )}
                {slotsError && (
                  <div className="flex items-center gap-2 text-amber-400 text-xs mt-3 text-center shrink-0">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {slotsError}
                  </div>
                )}

                {/* Time Slots for Selected Date */}
                <div className="mt-6 w-full shrink-0">
                  <div className="flex items-center gap-2 text-amber-400 font-black text-sm uppercase tracking-wider mb-3">
                    <Clock className="w-4 h-4 shrink-0" /> 2. Select Time Slot
                  </div>
                  {!selectedDate ? (
                    <p className="text-slate-500 text-sm text-center py-4">Select a date to see available times.</p>
                  ) : getSlotsForDate(selectedDate).length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">No slots available on this date. Try another day.</p>
                  ) : (
                    <div className="space-y-2">
                      {getSlotsForDate(selectedDate).map((slot) => (
                        <button
                          type="button"
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={`w-full text-left px-4 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center justify-between border ${
                            selectedSlot === slot
                              ? "bg-amber-400 text-slate-950 border-amber-400 shadow-md font-black"
                              : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"
                          }`}
                        >
                          <span>{formatSlotDisplay(slot)}</span>
                          {selectedSlot === slot && <CheckCircle className="w-4 h-4 text-slate-950 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Column - Full width on mobile, right column on desktop */}
              <div className="lg:col-span-7 flex flex-col space-y-6">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-primary font-black text-base sm:text-lg uppercase tracking-wider mb-4 pb-2 border-b border-slate-700 whitespace-nowrap overflow-hidden">
                    <User className="w-5 h-5 shrink-0" /> <span className="truncate">3. Enter Your Davie Location & Contact Info</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div className="space-y-2 min-w-0">
                      <Label className="text-slate-200 text-xs font-bold uppercase">First Name *</Label>
                      <Input
                        required
                        name="first_name"
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-11 sm:h-12"
                      />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <Label className="text-slate-200 text-xs font-bold uppercase">Last Name *</Label>
                      <Input
                        required
                        name="last_name"
                        placeholder="Smith"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-11 sm:h-12"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div className="space-y-2 min-w-0">
                      <Label className="text-slate-200 text-xs font-bold uppercase">Email *</Label>
                      <Input
                        required
                        type="email"
                        name="email"
                        placeholder="john@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-11 sm:h-12"
                      />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <Label className="text-slate-200 text-xs font-bold uppercase">Phone Number *</Label>
                      <Input
                        required
                        type="tel"
                        name="phone"
                        placeholder="(954) 555-0199"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-11 sm:h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 mb-4 min-w-0">
                    <Label className="text-slate-200 text-xs font-bold uppercase">Street Address / City / Zip Code *</Label>
                    <Input
                      required
                      name="address1"
                      placeholder="e.g. 8400 Orange Dr, Davie, FL 33328"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-11 sm:h-12"
                    />
                  </div>

                  <div className="space-y-2 mb-4 min-w-0">
                    <Label className="text-slate-200 text-xs font-bold uppercase">Type of Junk / Service Needed *</Label>
                    <Select value={serviceType} onValueChange={setServiceType}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white h-11 sm:h-12">
                        <SelectValue placeholder="Select type of junk to haul..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 text-white border-slate-700">
                        <SelectItem value="Furniture Removal">Furniture / Couch / Mattress Removal</SelectItem>
                        <SelectItem value="Appliance Removal">Appliance Pickup (Fridge, Washer, Dryer)</SelectItem>
                        <SelectItem value="Full Cleanout">Full Garage, House or Attic Cleanout</SelectItem>
                        <SelectItem value="Yard Waste">Yard Waste & Storm Debris</SelectItem>
                        <SelectItem value="Construction Debris">Construction & Renovation Debris</SelectItem>
                        <SelectItem value="Commercial Removal">Commercial / Office Cleanout</SelectItem>
                        <SelectItem value="Storage Unit">Storage Unit Cleanout</SelectItem>
                        <SelectItem value="Other Junk">Other Custom Junk Removal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 min-w-0">
                    <Label className="text-slate-200 text-xs font-bold uppercase">Additional Notes / Stairs / Access Details</Label>
                    <Textarea
                      placeholder="e.g., Couch on 2nd floor, easy driveway parking..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 min-h-[80px] sm:min-h-[100px]"
                    />
                  </div>
                </div>

                {/* Summary Box & Submit Button */}
                <div className="bg-slate-900 border border-slate-700 p-4 sm:p-5 rounded-2xl shrink-0">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-300 mb-4 pb-3 border-b border-slate-800 gap-2">
                    <span className="truncate">
                      Selected:{" "}
                      <strong className="text-amber-400">
                        {selectedDate?.toLocaleDateString() ?? "No date"}
                      </strong>
                      {selectedSlot && ` at ${formatSlotDisplay(selectedSlot)}`}
                    </span>
                    <span className="text-emerald-400 font-bold flex items-center gap-1 shrink-0">
                      <Sparkles className="w-3.5 h-3.5" /> Free On-Site Quote
                    </span>
                  </div>

                  {submitError && (
                    <div className="flex items-center gap-2 text-rose-400 text-sm mb-3">
                      <AlertCircle className="w-4 h-4 shrink-0" /> {submitError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting || !selectedSlot}
                    className="w-full h-12 sm:h-14 bg-primary hover:bg-rose-700 text-white font-black text-base sm:text-lg uppercase tracking-wider shadow-lg shadow-rose-600/30 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Reserving...</>
                    ) : (
                      <><Send className="w-5 h-5 mr-2" /> Reserve Pickup Slot</>
                    )}
                  </Button>
                  <p className="text-[10px] sm:text-[11px] text-center text-slate-400 mt-2">
                    No credit card required upfront. 100% free no-obligation estimate on arrival!
                  </p>
                </div>
              </div>

            </form>
          </div>
        )}

      </div>
    </section>
  );
};
